import { useState } from "react";
import { Search, Users, Star, Calendar, MapPin, GraduationCap, Bell, BellOff, Globe, ArrowLeft } from "lucide-react";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { useToast } from "../../hooks/use-toast";

const allUniversities = [
{ id: 1, name: "ADA University", location: "Baku", students: "3,200+", bio: "Leading private university focused on public policy, IT, engineering, and business. Known for innovative teaching and international partnerships.", initials: "AD", rating: 4.7, faculties: ["IT", "Engineering", "Business", "Public Policy"], isFollowing: true, events: [
  { title: "Open Day 2026", date: "Apr 12, 2026", location: "ADA Campus", attendees: 300, type: "Open Day" },
  { title: "Tech Startup Demo Day", date: "Apr 20, 2026", location: "Innovation Center", attendees: 100, type: "Conference" },
  { title: "Spring Career Fair", date: "May 5, 2026", location: "Main Hall", attendees: 500, type: "Career" }],
  recentPosts: 12 },
{ id: 2, name: "Baku State University", location: "Baku", students: "15,000+", bio: "The oldest and largest university in Azerbaijan, offering over 60 bachelor's and 80 master's programs across all major disciplines.", initials: "BS", rating: 4.5, faculties: ["Sciences", "Humanities", "Law", "Medicine", "Engineering"], isFollowing: true, events: [
  { title: "Scientific Conference 2026", date: "Mar 28, 2026", location: "BSU Main Campus", attendees: 200, type: "Conference" },
  { title: "Alumni Networking Night", date: "Apr 8, 2026", location: "Grand Hall", attendees: 150, type: "Career" }],
  recentPosts: 8 },
{ id: 3, name: "Baku Engineering University", location: "Baku", students: "5,500+", bio: "Premier engineering university in Azerbaijan with modern labs, ABET-accredited programs, and strong industry partnerships.", initials: "BE", rating: 4.4, faculties: ["Mechanical Eng.", "Civil Eng.", "Computer Eng.", "Chemical Eng."], isFollowing: false, events: [
  { title: "Engineering Expo 2026", date: "Apr 15, 2026", location: "BEU Campus", attendees: 250, type: "Exhibition" }],
  recentPosts: 5 },
{ id: 4, name: "Khazar University", location: "Baku", students: "2,800+", bio: "Private research university offering programs in engineering, economics, humanities, and education with international accreditation.", initials: "KH", rating: 4.3, faculties: ["Engineering", "Economics", "Humanities", "Education"], isFollowing: false, events: [
  { title: "Research Symposium", date: "May 10, 2026", location: "Khazar Campus", attendees: 80, type: "Conference" }],
  recentPosts: 3 },
{ id: 5, name: "ASOIU", location: "Baku", students: "8,000+", bio: "Azerbaijan State Oil and Industry University — a leading technical university specializing in petroleum engineering, IT, and industrial management.", initials: "AS", rating: 4.2, faculties: ["Petroleum Eng.", "IT", "Industrial Management", "Chemical Technology"], isFollowing: false, events: [
  { title: "Oil & Gas Innovation Forum", date: "Apr 25, 2026", location: "ASOIU Auditorium", attendees: 180, type: "Conference" },
  { title: "ASOIU Hackathon", date: "May 15, 2026", location: "IT Lab", attendees: 60, type: "Hackathon" }],
  recentPosts: 4 },
{ id: 6, name: "Azerbaijan University of Languages", location: "Baku", students: "4,000+", bio: "Specialized in linguistics, translation, and international relations with programs in 15+ languages.", initials: "AL", rating: 4.1, faculties: ["Linguistics", "Translation", "International Relations"], isFollowing: false, events: [], recentPosts: 1 }];


const eventTypes = ["All Events", "Conference", "Career", "Open Day", "Hackathon", "Exhibition"];

const UniversitiesDiscovery = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMode, setSearchMode] = useState("universities");
  const [selectedEventType, setSelectedEventType] = useState("All Events");
  const [following, setFollowing] = useState([1, 2]);
  const [notifications, setNotifications] = useState({ 1: true, 2: true });
  const [selectedUniId, setSelectedUniId] = useState(null);
  const { toast } = useToast();

  const filtered = allUniversities.filter((u) =>
  u.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const allEvents = allUniversities.flatMap((uni) =>
  uni.events.map((e) => ({ ...e, uniName: uni.name, uniId: uni.id, uniInitials: uni.initials }))
  );

  const filteredEvents = allEvents.filter((e) => {
    const matchesSearch = searchQuery.length < 2 || e.title.toLowerCase().includes(searchQuery.toLowerCase()) || e.uniName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedEventType === "All Events" || e.type === selectedEventType;
    return matchesSearch && matchesType;
  });

  const toggleFollow = (id, name) => {
    const isFollowed = following.includes(id);
    setFollowing((prev) => isFollowed ? prev.filter((f) => f !== id) : [...prev, id]);
    if (!isFollowed) {
      setNotifications((prev) => ({ ...prev, [id]: true }));
      toast({ title: `Following ${name}`, description: "You'll receive notifications about events and updates." });
    } else {
      setNotifications((prev) => {const n = { ...prev };delete n[id];return n;});
      toast({ title: `Unfollowed ${name}` });
    }
  };

  const toggleNotification = (id, e) => {
    e.stopPropagation();
    setNotifications((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const selectedUni = selectedUniId ? allUniversities.find((u) => u.id === selectedUniId) : null;

  // University Detail View
  if (selectedUni) {
    const isFollowed = following.includes(selectedUni.id);
    return (
      <div className="space-y-4">
        <div className="stat-card !p-5">
          <button onClick={() => setSelectedUniId(null)} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-3">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Universities
          </button>
          <div className="flex items-start gap-4">
            <Avatar className="w-14 h-14 flex-shrink-0">
              <AvatarFallback className="bg-primary/10 text-primary text-lg font-bold">{selectedUni.initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-display font-bold text-foreground">{selectedUni.name}</h2>
                <span className="flex items-center gap-0.5 text-xs text-muted-foreground">
                  <Star className="w-3.5 h-3.5 text-chart-4 fill-chart-4" /> {selectedUni.rating}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {selectedUni.location}</span>
                <span className="flex items-center gap-0.5"><Users className="w-3 h-3" /> {selectedUni.students}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{selectedUni.bio}</p>
              <div className="flex flex-wrap gap-1 mt-2">
                {selectedUni.faculties.map((f) =>
                <span key={f} className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">{f}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => toggleFollow(selectedUni.id, selectedUni.name)}
            className={`flex-1 h-9 rounded-xl text-xs font-semibold transition-colors flex items-center justify-center gap-1 ${
            isFollowed ? "bg-secondary text-secondary-foreground" : "bg-primary text-primary-foreground hover:bg-primary/90"}`
            }>
              {isFollowed ? "✓ Following" : "Follow"}
            </button>
            {isFollowed &&
            <button onClick={(e) => toggleNotification(selectedUni.id, e)}
            className={`h-9 w-9 rounded-xl flex items-center justify-center transition-colors ${
            notifications[selectedUni.id] ? "bg-secondary/10 text-secondary" : "bg-muted text-muted-foreground"}`
            }>
                {notifications[selectedUni.id] ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
              </button>
            }
          </div>
        </div>

        {/* University Events */}
        <div className="stat-card !p-4">
          <h3 className="text-sm font-display font-bold text-foreground mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-secondary" /> Events
          </h3>
          {selectedUni.events.length === 0 ?
          <p className="text-xs text-muted-foreground text-center py-4">No upcoming events</p> :

          <div className="space-y-2">
              {selectedUni.events.map((event, i) =>
            <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors">
                  <div className="w-11 h-11 rounded-xl bg-secondary/10 flex flex-col items-center justify-center flex-shrink-0">
                    <Calendar className="w-3.5 h-3.5 text-secondary mb-0.5" />
                    <span className="text-[8px] font-bold text-secondary">{event.date.split(",")[0].split(" ").slice(0, 2).join(" ")}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h4 className="text-sm font-semibold text-foreground">{event.title}</h4>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-chart-5/10 text-chart-5 font-bold uppercase">{event.type}</span>
                    </div>
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <MapPin className="w-3 h-3" />{event.location} · {event.attendees} attending
                    </p>
                  </div>
                  <button className="px-3 py-1.5 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors flex-shrink-0">
                    Attend
                  </button>
                </div>
            )}
            </div>
          }
        </div>
      </div>);

  }

  // List View
  return (
    <div className="space-y-4">
      <div className="stat-card !p-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="flex gap-1 p-1 rounded-xl bg-muted w-fit">
            <button onClick={() => setSearchMode("universities")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${searchMode === "universities" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
              Universities
            </button>
            <button onClick={() => setSearchMode("events")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${searchMode === "events" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
              Events
            </button>
            <button onClick={() => setSearchMode("my")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${searchMode === "my" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
              My Universities
            </button>
          </div>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={searchMode === "universities" ? "Search universities by name..." : "Search university events..."}
          className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-muted border-0 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
          
        </div>

        {searchMode === "events" &&
        <div className="flex flex-wrap gap-1.5">
            {eventTypes.map((type) =>
          <button key={type} onClick={() => setSelectedEventType(type)}
          className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${
          selectedEventType === type ? "bg-secondary/15 text-secondary" : "bg-muted text-muted-foreground hover:text-foreground"}`
          }>
                {type}
              </button>
          )}
          </div>
        }

        <p className="text-xs text-muted-foreground">
          {searchMode === "universities" ? `${filtered.length} universit${filtered.length !== 1 ? "ies" : "y"}` : `${filteredEvents.length} event${filteredEvents.length !== 1 ? "s" : ""}`}
        </p>
      </div>

      {searchMode === "events" ?
      <div className="space-y-3">
          {filteredEvents.length === 0 ?
        <div className="stat-card !p-8 text-center">
              <Calendar className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
              <p className="text-sm font-medium text-muted-foreground">No events found</p>
            </div> :

        filteredEvents.map((event, i) =>
        <div key={i} className="stat-card !p-4 flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-secondary/10 flex flex-col items-center justify-center flex-shrink-0">
                  <Calendar className="w-4 h-4 text-secondary mb-0.5" />
                  <span className="text-[9px] font-bold text-secondary">{event.date.split(",")[0].split(" ").slice(0, 2).join(" ")}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <h4 className="text-sm font-display font-bold text-foreground">{event.title}</h4>
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-chart-5/10 text-chart-5 font-bold uppercase">{event.type}</span>
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <button onClick={() => setSelectedUniId(event.uniId)} className="px-1.5 py-0.5 rounded-md bg-primary/10 text-primary font-medium hover:bg-primary/20 transition-colors">{event.uniName}</button>
                    <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{event.location}</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{event.date} · {event.attendees} attending</p>
                </div>
                <button className="px-3 py-1.5 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors flex-shrink-0">
                  Attend
                </button>
              </div>
        )
        }
        </div> :

      <div className="space-y-3">
          {filtered.map((uni) => {
          const isFollowed = following.includes(uni.id);
          return (
            <div key={uni.id} className="stat-card !p-4">
                <div className="flex items-start gap-3">
                  <button onClick={() => setSelectedUniId(uni.id)}>
                    <Avatar className="w-12 h-12 flex-shrink-0">
                      <AvatarFallback className="bg-primary/10 text-primary text-sm font-bold">{uni.initials}</AvatarFallback>
                    </Avatar>
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setSelectedUniId(uni.id)} className="text-sm font-display font-bold text-foreground hover:text-secondary transition-colors">{uni.name}</button>
                      <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground ml-auto">
                        <Star className="w-3 h-3 text-chart-4 fill-chart-4" /> {uni.rating}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                      <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {uni.location}</span>
                      <span className="flex items-center gap-0.5"><Users className="w-3 h-3" /> {uni.students}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed line-clamp-2">{uni.bio}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {uni.faculties.map((f) =>
                    <span key={f} className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">{f}</span>
                    )}
                    </div>
                    {uni.events.length > 0 &&
                  <div className="mt-3 space-y-1.5">
                        {uni.events.slice(0, 2).map((event, i) =>
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-muted/50 text-xs">
                            <Calendar className="w-3 h-3 text-secondary flex-shrink-0" />
                            <span className="font-medium text-foreground truncate flex-1">{event.title}</span>
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-chart-5/10 text-chart-5 font-medium">{event.type}</span>
                            <span className="text-muted-foreground text-[10px] flex-shrink-0">{event.date.split(",")[0]}</span>
                          </div>
                    )}
                      </div>
                  }
                    {uni.recentPosts > 0 &&
                  <p className="text-[10px] text-secondary mt-2 flex items-center gap-1">
                        <Globe className="w-3 h-3" /> {uni.recentPosts} new posts this week
                      </p>
                  }
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                  <button onClick={() => toggleFollow(uni.id, uni.name)}
                className={`flex-1 h-8 rounded-xl text-xs font-semibold transition-colors flex items-center justify-center gap-1 ${
                isFollowed ? "bg-secondary text-secondary-foreground" : "bg-primary text-primary-foreground hover:bg-primary/90"}`
                }>
                    {isFollowed ? "✓ Following" : "Follow"}
                  </button>
                  {isFollowed &&
                <button onClick={(e) => toggleNotification(uni.id, e)}
                className={`h-8 w-8 rounded-xl flex items-center justify-center transition-colors ${
                notifications[uni.id] ? "bg-secondary/10 text-secondary" : "bg-muted text-muted-foreground"}`
                }>
                      {notifications[uni.id] ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
                    </button>
                }
                  <button onClick={() => setSelectedUniId(uni.id)}
                className="h-8 px-3 rounded-xl bg-muted text-xs font-medium text-foreground hover:bg-muted/80 transition-colors">
                    View
                  </button>
                </div>
              </div>);

        })}
          {filtered.length === 0 &&
        <div className="stat-card !p-8 text-center">
              <GraduationCap className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
              <p className="text-sm font-medium text-muted-foreground">No universities found</p>
            </div>
        }
        </div>
      }

      {/* My Universities View */}
      {searchMode === "my" &&
      <div className="space-y-3">
          {(() => {
          const myUnis = allUniversities.filter((u) => following.includes(u.id));
          const filteredMyUnis = myUnis.filter((u) => u.name.toLowerCase().includes(searchQuery.toLowerCase()));
          if (filteredMyUnis.length === 0) return (
            <div className="stat-card !p-8 text-center">
                <GraduationCap className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
                <p className="text-sm font-medium text-muted-foreground">You're not following any universities yet</p>
              </div>);

          return filteredMyUnis.map((uni) =>
          <div key={uni.id} className="stat-card !p-4">
                <div className="flex items-start gap-3">
                  <button onClick={() => setSelectedUniId(uni.id)}>
                    <Avatar className="w-11 h-11 flex-shrink-0">
                      <AvatarFallback className="bg-primary/10 text-primary text-sm font-bold">{uni.initials}</AvatarFallback>
                    </Avatar>
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setSelectedUniId(uni.id)} className="text-sm font-display font-bold text-foreground hover:text-secondary transition-colors">{uni.name}</button>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-secondary/15 text-secondary font-bold">Following</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{uni.location} · {uni.students} students</p>
                    {uni.events.length > 0 &&
                <div className="mt-2 space-y-1">
                        {uni.events.slice(0, 2).map((event, i) =>
                  <div key={i} className="flex items-center gap-2 p-1.5 rounded-lg bg-muted/50 text-xs">
                            <Calendar className="w-3 h-3 text-secondary flex-shrink-0" />
                            <span className="font-medium text-foreground truncate flex-1">{event.title}</span>
                            <span className="text-muted-foreground text-[10px]">{event.date.split(",")[0]}</span>
                          </div>
                  )}
                      </div>
                }
                  </div>
                  <button onClick={(e) => toggleNotification(uni.id, e)}
              className={`h-8 w-8 rounded-xl flex items-center justify-center transition-colors flex-shrink-0 ${
              notifications[uni.id] ? "bg-secondary/10 text-secondary" : "bg-muted text-muted-foreground"}`
              }>
                    {notifications[uni.id] ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
          );
        })()}
        </div>
      }
    </div>);

};

export default UniversitiesDiscovery;